import type { Texture } from 'three'
import {
  EventDispatcher,
  LinearSRGBColorSpace,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
  EquirectangularReflectionMapping,
  Vector2,
} from 'three'
import { CSS2DRenderer, CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js'
import type { Hotspot, HotspotType } from '@/entities/site'
import { getVfovBounds, uvToWorld } from '@/lib/site-viewer/utils'
import { radToDeg, clamp } from 'three/src/math/MathUtils'
import { MAX_FOV, SPHERE_RADIUS } from '@/lib/site-viewer/constants'
import hotspotPin from '~/assets/images/viewer-icons/hotspot-pin.png'
import threeDPin from '~/assets/images/viewer-icons/threed-pin.png'
import mapPin from '~/assets/images/viewer-icons/map-pin.png'
import pdfPin from '~/assets/images/viewer-icons/pdf-pin.png'
import photoPin from '~/assets/images/viewer-icons/photo-pin.png'
import videoPin from '~/assets/images/viewer-icons/video-pin.png'
import infoPin from '~/assets/images/viewer-icons/info-pin.png'

export enum ViewEvent {
  FovClamped = 'fov-clamped',
  HotspotClicked = 'hotspot-clicked',
  HotspotRightClicked = 'hotspot-rightclicked',
  Resize = 'resize-event',
}

export type HotspotRightClickEvent = {
  hotspot: Hotspot
  event: PointerEvent
}

type ViewerEvent = {
  [ViewEvent.Resize]: { payload: Size }
  [ViewEvent.HotspotClicked]: { hotspot: Hotspot }
  [ViewEvent.HotspotRightClicked]: HotspotRightClickEvent
  [ViewEvent.FovClamped]: { fov: number }
}

class SiteViewer extends EventDispatcher<ViewerEvent> {
  renderer: WebGLRenderer
  cssRenderer: CSS2DRenderer
  scene: Scene
  overlayScene: Scene
  camera: PerspectiveCamera
  resizeObserver: ResizeObserver
  private _onContextRestored: () => void
  private _disposed: boolean
  private _suppressWarning: boolean
  private _vfovBounds?: { min: number; max: number }
  private _background: Texture | null
  private _targetFov: number = radToDeg(MAX_FOV)

  constructor(resizeContainer: HTMLElement) {
    super()

    const { width, height } = resizeContainer.getBoundingClientRect()

    this.scene = new Scene()
    // second scene for text labels
    this.overlayScene = new Scene()

    this._background = null
    this._disposed = false
    this._suppressWarning = false
    this._onContextRestored = () => {
      // For some reason we need to trigger... something... to get it to redraw.
      // This works and so does resizing the window (but can't do that programmatically)
      resizeContainer.appendChild(this.renderer.domElement)
      resizeContainer.appendChild(this.cssRenderer.domElement)
      this.render()
    }

    const canvas = document.createElement('canvas')
    resizeContainer.appendChild(canvas)

    this.camera = new PerspectiveCamera(MAX_FOV, width / height, 1, SPHERE_RADIUS)
    this._updateFov(width, height)
    this.scene.add(this.camera)

    this.renderer = new WebGLRenderer({ alpha: true, antialias: true, canvas })
    // Must be attached after the renderer is created so it runs after three.js's restored handler
    this.renderer.domElement.addEventListener('webglcontextrestored', this._onContextRestored)
    this.renderer.domElement.style.touchAction = 'none'
    this.renderer.outputColorSpace = LinearSRGBColorSpace

    this.cssRenderer = new CSS2DRenderer()
    this.cssRenderer.domElement.className = 'hotspot-layer'
    this.cssRenderer.domElement.style.touchAction = 'none'
    resizeContainer.appendChild(this.cssRenderer.domElement)

    this.setSize(width, height)

    // Watch for resize of the container and resize canvas accordingly
    this.resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      const { width, height } = entries[0].contentRect
      this.setSize(width, height)
    })
    this.resizeObserver.observe(resizeContainer)
  }

  setSize(width: number, height: number) {
    // Ensure size is an integer and at least 1 x 1
    // Non-integer values can cause blurring
    width = Math.floor(Math.max(1, width))
    height = Math.floor(Math.max(1, height))

    this.renderer.setPixelRatio(window.devicePixelRatio)
    this.renderer.setSize(width, height)
    this.cssRenderer.setSize(width, height)

    this.camera.aspect = width / height
    this._updateFov(width, height)
    // render immediately to prevent flickering on resize
    this.render()

    this.dispatchEvent({ type: ViewEvent.Resize, payload: { width, height } })
  }

  setBackground(texture: Texture) {
    this._background = texture
    texture.mapping = EquirectangularReflectionMapping
    this.scene.background = texture

    const size = this.renderer.getSize(new Vector2()).multiplyScalar(window.devicePixelRatio)
    this._updateFov(size.x, size.y)
  }

  setFov(fov: number) {
    this._targetFov = fov
    this.camera.fov = this.clampFov(this._targetFov)
    this.camera.updateProjectionMatrix()
  }

  private _updateFov(width: number, height: number): void {
    this._vfovBounds = getVfovBounds(MAX_FOV, width, height)
    this.camera.fov = this.clampFov(this._targetFov)
    this.camera.updateProjectionMatrix()
    this.dispatchEvent({ type: ViewEvent.FovClamped, fov: this.camera.fov })
  }

  clampFov(fov: number): number {
    if (!this._vfovBounds) return fov
    return clamp(fov, radToDeg(this._vfovBounds.min), radToDeg(this._vfovBounds.max))
  }

  render() {
    if (this._disposed) {
      if (!this._suppressWarning) {
        console.warn('SiteViewer.render() called after SiteViewer.dispose()')
        this._suppressWarning = true
      }
      return
    }

    // Update device pixel ratio if needed. Can happen when moving window between monitors with different dpis
    if (window.devicePixelRatio !== this.renderer.getPixelRatio()) {
      this.renderer.setPixelRatio(window.devicePixelRatio)
    }

    this.renderer.render(this.scene, this.camera)
    this.cssRenderer.render(this.overlayScene, this.camera)
  }

  addHotspots(hotspots: Hotspot[]) {
    if (!hotspots) return

    try {
      hotspots.forEach((hotspot: Hotspot) => {
        const hotspotObject = this.createAndPositionHotspot(hotspot)

        this.overlayScene.add(hotspotObject)
      })
    } catch (e) {
      console.error(e)
    }
  }

  removeHotspots() {
    this.overlayScene.clear()
  }

  createAndPositionHotspot(hotspot: Hotspot) {
    if (!this._background) throw new Error('Background does not exist')

    let uv = {
      x: hotspot.pos_x,
      y: 1 - hotspot.pos_y + hotspot.y_offset / this._background.image.height,
    }

    const hotspotContainer = this.createHotspotContainer()
    const hotspotLabel = this.createHotspotLabel(hotspot.title, hotspot.status)
    const hotspotIcon = this.createHotspotIcon(hotspot)

    hotspotContainer.appendChild(hotspotLabel)
    hotspotContainer.appendChild(hotspotIcon)

    const hotspotObject = new CSS2DObject(hotspotContainer)
    const worldCoordinates = uvToWorld(uv)
    hotspotObject.position.copy(worldCoordinates).multiplyScalar(SPHERE_RADIUS - 10)

    return hotspotObject
  }

  createHotspotContainer() {
    const container = document.createElement('div')
    container.className = 'hotspot-container'

    return container
  }

  createHotspotIcon(hotspot: Hotspot) {
    const icon = document.createElement('img')
    icon.classList.add('hotspot-icon')
    icon.src = this.getHotspotImageUrl(hotspot.media[0].type)

    icon.addEventListener('pointerdown', e => {
      e.preventDefault()

      if (e.button === 0) {
        this.dispatchEvent({ type: ViewEvent.HotspotClicked, hotspot })
      }

      const RIGHT_CLICK = 2
      if (e.button === RIGHT_CLICK) {
        this.dispatchEvent({ type: ViewEvent.HotspotRightClicked, hotspot, event: e })
      }
    })

    return icon
  }

  createHotspotLabel(title: string, status: string) {
    const label = document.createElement('div')
    label.classList.add('hotspot-label')
    if (status === 'U') {
      label.classList.add('private-hotspot')
    }

    const text = document.createTextNode(`${title}`)
    label.appendChild(text)

    return label
  }

  getHotspotImageUrl(type: HotspotType) {
    switch (type) {
      case 'L':
        return hotspotPin
      case '3':
        return threeDPin
      case 'M':
        return mapPin
      case 'P':
        return pdfPin
      case 'I':
        return photoPin
      case 'V':
        return videoPin
      case 'N':
        return infoPin
    }
  }

  dispose() {
    this.resizeObserver.disconnect()
    this.renderer.domElement.removeEventListener('webglcontextrestored', this._onContextRestored)
    this.renderer.dispose() // disposes all resources associated with this renderer

    // Renderers should not be used after calling dispose() because their context loss handlers are removed along with other event handlers
    this._disposed = true
  }
}

export { SiteViewer }
